/**
 * The self-hosted proof of work (user-management §2.10): find a nonce so
 * that sha256(challenge + ":" + nonce) has `difficulty` leading zero bits.
 * Runs in small batches so the page stays responsive.
 */
import type { PowChallengeResponse, PowSolution } from '@neosocial/protocol';

export function leadingZeroBits(bytes: Uint8Array): number {
  let n = 0;
  for (const b of bytes) {
    if (b === 0) {
      n += 8;
      continue;
    }
    n += Math.clz32(b) - 24;
    break;
  }
  return n;
}

export async function solvePow(
  c: PowChallengeResponse,
  signal?: AbortSignal,
): Promise<PowSolution> {
  const enc = new TextEncoder();
  let nonce = 0;
  for (;;) {
    for (let i = 0; i < 2000; i++, nonce++) {
      const h = new Uint8Array(
        await crypto.subtle.digest('SHA-256', enc.encode(`${c.challenge}:${nonce}`)),
      );
      if (leadingZeroBits(h) >= c.difficulty)
        return { challenge: c.challenge, nonce: String(nonce) };
    }
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    await new Promise((r) => setTimeout(r, 0));
  }
}
